import React, { useEffect, useMemo, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useIntl } from 'react-intl';
import SingleColumnTemplate from '../../../../ui/layouts/SingleColumnTemplate.jsx';
import ProgramCard from '../../components/coachLed/ProgramCard.jsx';
import ProgramCardEnrolled from '../../components/coachLed/ProgramCardEnrolled.jsx';
import ProgramFilters from '../../components/coachLed/ProgramFilters.jsx';

const PROGRAMS_QUERY = gql`
  query SprintPrograms($status: String, $goal: String, $level: String, $featured: Boolean, $isGroup: Boolean, $search: String) {
    sprintPrograms(status: $status, goal: $goal, level: $level, featured: $featured, is_group: $isGroup, search: $search) {
      id
      title
      slug
      summary
      level
      price_cents
      featured
      hero_image_url
      goals
      duration_days
      avg_rating
      enrollment_count
      is_group
      coachProfile { id name headline avatar_url verified rating review_count }
    }
  }
`;

const ENROLLMENTS_QUERY = gql`
  query MySprintEnrollments {
    mySprintEnrollments {
      id
      status
      progress_pct
      completed_task_count
      total_task_count
      started_on
      target_end_on
      sprintProgram {
        id
        title
        slug
        summary
        level
        price_cents
        hero_image_url
        duration_days
        avg_rating
        is_group
        goals
        coachProfile { id name headline avatar_url verified rating review_count }
      }
    }
  }
`;

export default function MarketplaceHomeContainer() {
  const intl = useIntl();
  const [filters, setFilters] = useState({
    search: '',
    goals: [],
    levels: [],
    isGroup: null,
    sortBy: 'popular',
  });
  const { data, loading, error } = useQuery(PROGRAMS_QUERY, {
    variables: {
      status: 'published',
      goal: filters.goals.length === 1 ? filters.goals[0] : null,
      level: filters.levels.length === 1 ? filters.levels[0] : null,
      isGroup: filters.isGroup,
      search: filters.search || null,
    },
  });

  const { data: enrollmentsData, loading: enrollmentsLoading } = useQuery(ENROLLMENTS_QUERY);

  // Track filter changes
  useEffect(() => {
    try {
      if (window.ahoy && typeof window.ahoy.track === 'function') {
        window.ahoy.track('marketplace_filters_change', {
          search: filters.search,
          goals: filters.goals,
          levels: filters.levels,
          is_group: filters.isGroup,
          sort_by: filters.sortBy,
        });
      }
    } catch (_) {}
  }, [filters.search, filters.goals, filters.levels, filters.isGroup, filters.sortBy]);

  const programs = data?.sprintPrograms || [];
  const allEnrollments = enrollmentsData?.mySprintEnrollments || [];

  // Filter enrollments based on current filters
  const filteredEnrollments = useMemo(() => {
    return allEnrollments.filter((enrollment) => {
      if (!enrollment.sprintProgram) return false;
      const program = enrollment.sprintProgram;

      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesTitle = program.title?.toLowerCase().includes(searchLower);
        const matchesSummary = program.summary?.toLowerCase().includes(searchLower);
        const matchesCoach = program.coachProfile?.name?.toLowerCase().includes(searchLower);
        if (!matchesTitle && !matchesSummary && !matchesCoach) return false;
      }

      // Goals filter
      if (filters.goals.length > 0) {
        const programGoals = program.goals || [];
        const hasMatchingGoal = filters.goals.some((goal) => programGoals.includes(goal));
        if (!hasMatchingGoal) return false;
      }

      // Level filter
      if (filters.levels.length > 0) {
        if (!filters.levels.includes(program.level)) return false;
      }

      // Group/1:1 filter
      if (filters.isGroup !== null) {
        if (program.is_group !== filters.isGroup) return false;
      }

      return true;
    });
  }, [allEnrollments, filters]);

  // Create a map of enrolled program IDs to enrollment data
  const enrollmentsByProgramId = useMemo(() => {
    const map = {};
    allEnrollments.forEach((enrollment) => {
      if (enrollment.sprintProgram) {
        map[enrollment.sprintProgram.id] = enrollment;
      }
    });
    return map;
  }, [allEnrollments]);

  // Separate featured programs for the featured section
  const featuredPrograms = useMemo(() => {
    return programs.filter((p) => p.featured);
  }, [programs]);

  const programCounts = useMemo(() => {
    return {
      total: programs.length,
      byGoal: {
        grow: programs.filter((p) => (p.goals || []).includes('grow')).length,
        exit: programs.filter((p) => (p.goals || []).includes('exit')).length,
        protect: programs.filter((p) => (p.goals || []).includes('protect')).length,
      },
      byLevel: {
        starter: programs.filter((p) => p.level === 'starter').length,
        intermediate: programs.filter((p) => p.level === 'intermediate').length,
        advanced: programs.filter((p) => p.level === 'advanced').length,
      },
    };
  }, [programs]);

  const sorted = useMemo(() => {
    const list = [...programs];
    switch (filters.sortBy) {
      case 'newest':
        return list.sort((a, b) => Number(b.id) - Number(a.id));
      case 'rating':
        return list.sort((a, b) => (b.avg_rating || 0) - (a.avg_rating || 0));
      case 'price-low':
        return list.sort((a, b) => (a.price_cents || 0) - (b.price_cents || 0));
      case 'price-high':
        return list.sort((a, b) => (b.price_cents || 0) - (a.price_cents || 0));
      case 'popular':
      default:
        return list.sort((a, b) => (b.enrollment_count || 0) - (a.enrollment_count || 0));
    }
  }, [programs, filters.sortBy]);

  return (
    <SingleColumnTemplate
      title={intl.formatMessage({ id: 'sprints.marketplace.title' })}
      subtitle={intl.formatMessage({ id: 'sprints.marketplace.subtitle' })}
      variant="wide"
    >
      <div className="rounded-2xl border border-border bg-card p-6 mb-6">
        <ProgramFilters filters={filters} onFiltersChange={setFilters} programCounts={programCounts} />
      </div>

      {loading && <div className="rounded-2xl border border-border bg-card p-6">{intl.formatMessage({ id: 'sprints.marketplace.loading' })}</div>}
      {error && <div className="rounded-2xl border border-border bg-card p-6">{intl.formatMessage({ id: 'sprints.marketplace.error' })}</div>}

      {!loading && !error && (
        <>
          {/* My Enrolled Programs Section */}
          {!enrollmentsLoading && filteredEnrollments.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{intl.formatMessage({ id: 'sprints.marketplace.enrolled.heading' })}</h2>
                  <p className="text-sm text-gray-600 mt-1">{intl.formatMessage({ id: 'sprints.marketplace.enrolled.subheading' })}</p>
                </div>
                <a
                  href="/sprints"
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg border border-succession-border bg-succession-bg-primary text-succession-text-primary hover:bg-succession-hover-bg transition-colors"
                >
                  {intl.formatMessage({ id: 'sprints.marketplace.enrolled.my_sprints' })}
                </a>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredEnrollments.map((enrollment) => (
                  <ProgramCardEnrolled
                    key={enrollment.id}
                    program={enrollment.sprintProgram}
                    coach={enrollment.sprintProgram.coachProfile}
                    enrollment={enrollment}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Featured Programs Section */}
          {featuredPrograms.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{intl.formatMessage({ id: 'sprints.marketplace.featured.heading' })}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {featuredPrograms.map((p) => {
                  const enrollment = enrollmentsByProgramId[p.id];
                  return enrollment ? (
                    <ProgramCardEnrolled
                      key={p.id}
                      program={p}
                      coach={p.coachProfile}
                      enrollment={enrollment}
                    />
                  ) : (
                    <ProgramCard key={p.id} program={p} coach={p.coachProfile} />
                  );
                })}
              </div>
            </div>
          )}

          {/* All Programs Section */}
          {sorted.length > 0 && (
            <div>
              {(featuredPrograms.length > 0 || filteredEnrollments.length > 0) && (
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{intl.formatMessage({ id: 'sprints.marketplace.all.heading' })}</h2>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {sorted.map((p) => {
                  const enrollment = enrollmentsByProgramId[p.id];
                  return enrollment ? (
                    <ProgramCardEnrolled
                      key={p.id}
                      program={p}
                      coach={p.coachProfile}
                      enrollment={enrollment}
                    />
                  ) : (
                    <ProgramCard key={p.id} program={p} coach={p.coachProfile} />
                  );
                })}
              </div>
            </div>
          )}

          {sorted.length === 0 && (
            <div className="rounded-2xl border border-border bg-card p-6 text-center text-gray-600">
              {intl.formatMessage({ id: 'sprints.marketplace.empty' })}
            </div>
          )}
        </>
      )}
    </SingleColumnTemplate>
  );
}


